import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  TextField,
  Box,
  Fab,
  Divider,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Typography,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { CaseTagEditor } from "./components/CaseTagEditor";
import { getCaseById, updateCase } from "../../features/slices/Case";
import Title from "../../components/Title";
import { AppDispatch } from "../../store";
import { useDispatch } from "react-redux";
import { useNotification } from "../../context/NotificationContext";
import { useLoader } from "../../context/LoaderContext";
import { JsonEditor } from "json-edit-react";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { Case } from "../../interfaces/Case";

const CaseEdit = () => {
  const { caseId } = useParams();
  const [singleCase, setCase] = useState<Case>({
    title: "",
    tc_result: "",
    sys_result: "",
    file_name: "",
    original_path: "",
    meta_data: {},
    manifest: {},
  } as Case);
  const { notify } = useNotification();
  const { showLoader, hideLoader } = useLoader();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const fetchSuite = useCallback(
    debounce(async () => {
      showLoader();
      try {
        const result = await dispatch(getCaseById(Number(caseId)) as any);
        hideLoader();
        if (result.payload) {
          setCase(result.payload);
        }
      } catch (error) {
        hideLoader();
        console.error("Failed to fetch suite", error);
        notify("Failed to fetch suite", "error");
      }
    }, 300),
    [dispatch, caseId]
  );

  useEffect(() => {
    fetchSuite();
  }, [dispatch, caseId]);
  const handleChangeStatus = (event: SelectChangeEvent) => {
    setCase({ ...singleCase, tc_result: event.target.value });
  };
  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setCase((prevCase) => ({
      ...prevCase,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    showLoader();
    try {
      const result = await dispatch(
        updateCase({
          title: singleCase.title,
          tc_result: singleCase.tc_result,
          manifest: singleCase.manifest,
          tool_data: singleCase.tool_data,
          meta_data: singleCase.meta_data,
          original_path: singleCase.original_path,
          file_name: singleCase.file_name,
          suite_id: singleCase.suite_id,
          id: Number(caseId),
        })
      );
      if (result.type === "case/updateCase/fulfilled") {
        notify("Case updated successfully", "success");
        navigate("/cases", { replace: true });
        hideLoader();
      } else {
        notify("Failed to update case", "error");
        hideLoader();
      }
    } catch (error) {
      hideLoader();
      console.error("Failed to update case", error);
      notify("Failed to update case", "error");
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{
        mt: 1,
        width: "100%", maxWidth: 760,
        justifyContent: "center",
        margin: "auto",
        flexWrap: "wrap",
      }}
    >
      <Title>Edit Case {caseId}</Title>
      <TextField
        margin="normal"
        required
        fullWidth
        id="title"
        label="Title"
        name="title"
        value={singleCase.title}
        onChange={handleChange}
        autoFocus
      />

      <FormControl fullWidth>
        <InputLabel id="status-label">TC Result</InputLabel>

        <Select
          required
          labelId="status-label"
          id="demo-simple-select"
          value={singleCase.tc_result}
          label="TC Result"
          onChange={handleChangeStatus}
        >
          <MenuItem value={"PASSED"}>Passed</MenuItem>
          <MenuItem value={"PASSED_HW"}>Passed HW</MenuItem>
          <MenuItem value={"FAILED_ENSURE"}>Failed Ensure</MenuItem>
          <MenuItem value={"FAILED"}>Failed</MenuItem>
          <MenuItem value={"INITIAL_CRASH"}>Initial Crash</MenuItem>
          <MenuItem value={"CRASHED"}>Crashed</MenuItem>
          <MenuItem value={"INIT"}>INIT</MenuItem>
        </Select>
      </FormControl>
      <TextField
        margin="normal"
        fullWidth
        name="result"
        label="Sys Result"
        type="text"
        id="result"
        disabled
        value={singleCase.sys_result}
      />
      <Divider sx={{ mt: 2, mb: 2 }} />
      <Typography fontWeight={"bold"}>Manifest</Typography>
      <JsonEditor
        minWidth={"100%"}
        data={singleCase.manifest ?? {}}
        collapse={1}
        onUpdate={(value) => setCase({ ...singleCase, manifest: value })}
      />
      <Divider sx={{ mt: 2, mb: 2 }} />
      <Typography fontWeight={"bold"}>Tool Data</Typography>
      <JsonEditor
        minWidth={"100%"}
        data={singleCase.tool_data ?? {}}
        collapse={1}
        onUpdate={(value) => setCase({ ...singleCase, tool_data: value })}
      />
      <Divider sx={{ mt: 2, mb: 2 }} />
      <CaseTagEditor caseId={caseId} />
      <Fab
        color="primary"
        variant="extended"
        sx={{ position: "fixed", bottom: 15, right: 15 }}
        onClick={handleSubmit}
        disabled={singleCase.title === "" || singleCase.tc_result === ""}
      >
        Update Case
      </Fab>
      <Fab
        color="primary"
        variant="extended"
        sx={{ position: "fixed", bottom: 15, left: 75 }}
        onClick={() => navigate(-1)}
      >
        Cancel
      </Fab>
    </Box>
  );
};

export default CaseEdit;
